if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.selectedVariant = this.getSelectedVariant(null, true);
        this.updateVariantIdAttributes(this.selectedVariant?.id);
        this.updateVariantMeta(this.selectedVariant);
        this.updateSelectedVariant(this.selectedVariant);
      }

      updateSelectedVariant(selectedVariant) {
        const ELM_formInput = this.querySelectorAll('.product-form__input');
        if (ELM_formInput) {
          ELM_formInput.forEach((elm, idx) => {
            if (elm && elm.querySelector('[data-value-variant]')) {
              elm.querySelector('[data-value-variant]').textContent = selectedVariant?.options[idx];
            }
          });
        }
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // Optimistically resolve the variant client-side so the variant ID
        // and price update instantly, without waiting for the section fetch.
        if (!shouldSwapProduct) {
          const matched = this.resolveVariantFromOptions();
          if (matched) {
            this.updateVariantInputs(matched.id);
            this.productForm?.toggleSubmitButton(!matched.available, window.variantStrings.soldOut, matched);
          }
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      /**
       * Match the currently selected options against window.variants to find
       * the variant without a network request.
       */
      resolveVariantFromOptions() {
        try {
          const variants = typeof window.variants === 'string'
            ? JSON.parse(window.variants)
            : window.variants;
          if (!Array.isArray(variants)) return null;

          // Collect the selected value text from each option fieldset
          const selects = this.variantSelectors;
          if (!selects) return null;
          const selected = Array.from(selects.querySelectorAll('fieldset')).map((fs) => {
            const checked = fs.querySelector('input:checked');
            if (checked) return checked.value;
            const sel = fs.querySelector('select');
            return sel ? sel.value : null;
          });

          return variants.find((v) =>
            v.options && v.options.length === selected.length &&
            v.options.every((opt, i) => opt === selected[i])
          ) || null;
        } catch {
          return null;
        }
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
        // Disable submit button immediately so the user cannot add the old variant
        // while the new variant data is being fetched. It will be re-enabled once
        // the fetch completes in handleUpdateProductInfo / setUnavailable.
        const submitBtn = productForm?.submitButton;
        if (submitBtn) submitBtn.setAttribute('disabled', 'disabled');
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            const selectedVariant = this.getSelectedVariant(html, false);
            this.updateSelectedVariant(selectedVariant);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode, isFirst) {
        let selectedVariant = null;
        if (isFirst) {
          selectedVariant = document.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        } else {
          selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        }
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantIdAttributes(variant?.id);
          this.updatevariantInventory(variant);
          this.updateVariantMeta(variant);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('MediaBadge');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Sticky-Variant');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut,
            variant
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateVariantIdAttributes(variantId) {
        this.querySelectorAll('[data-variant-id]').forEach((el) => {
          el.dataset.variantId = variantId ?? '';
        });
      }

      updatevariantInventory(variant) {
        const inventoryQuantity = variant.inventory_quantity ?? (window.inv_qty?.[variant.id] ?? 0);
        if (inventoryQuantity > 20) {
          this.querySelector('#pdp-delivery-info-status').innerHTML = "<span class='title'>In Stock: </span> Ships ";
        } else if (inventoryQuantity > 0 && inventoryQuantity <= 20) {
          this.querySelector('#pdp-delivery-info-status').innerHTML = "<span class='title'>Low Stock :</span> Ships ";
        } else {
          this.querySelector('#pdp-delivery-info-status').innerHTML = "<span class='title'>Available to Order :</span> Leaves our Sydney Warehouse "
        }
      }

      updateVariantMeta(variant) {
        if (!variant) return;

        const variants = window.variantsEta;
        const thisProductTags = window.thisProductTags ?? [];
        const inventoryQuantity = variant.inventory_quantity ?? (window.inv_qty?.[variant.id] ?? 0);
        const inventoryPolicy = variant.inventory_policy ?? (window.inv_policy?.[variant.id] ?? 'deny');
        const eta = variants?.[variant.id];
        const isMaProduct = thisProductTags.includes('production: Yes');
        const isUnitex = thisProductTags.includes('unitex-10-14');

        const ELM_dispatchDate = this.querySelector('#dispatch_date');
        const ELM_stockEta = this.querySelector('#stock_eta');
        const ELM_pdpDeliveryInfoValue = this.querySelector('#pdp-delivery-info-value');
        const ELM_pdpDeliveryInfo = this.querySelector('#pdp-delivery-info');
        const ELM_pdpDeliveryInfoSection = this.querySelector('.pdp-delivery-info-section');
        const ELM_pdpSoldOutInfo = this.querySelector('#pdp-sold-out-info');
        const ELM_dispatchInfo = this.querySelector('#dispatch_info');
        const ELM_dispatchInfoSoldout = this.querySelector('#dispatch_info_soldout');
        const ELM_preOrderText = this.querySelector('#preOrderText');
        const ELM_pdpDeliveryInfoStatus = this.querySelector('#pdp-delivery-info-status');
        const ELM_inputProperties = this.querySelector('.product-variant-eta-date');

        let deliveryTime = isUnitex ? '10-14 business days' : '1-3 business days';
        let timeDifferenceInDays = -1;

        ELM_stockEta && (ELM_stockEta.innerHTML = eta ?? '');

        if (eta?.length > 0 && inventoryQuantity <= 0) {
          const [y1, m1, d1] = new Date().toISOString().slice(0, 10).split('-');
          const [d2, m2, y2] = eta.split('/');
          const date1 = new Date(Number(y1), Number(m1) - 1, Number(d1));
          const date2 = new Date(Number(y2), Number(m2) - 1, Number(d2));
          timeDifferenceInDays = (date2 - date1) / (1000 * 60 * 60 * 24);

          const DELIVERY_RANGES = [
            { min: 3, max: 8, inclusive: true },
            { min: 8, max: 14, inclusive: false },
            { min: 14, max: 21, inclusive: false },
            { min: 21, max: 35, inclusive: false },
            { min: 35, max: 70, inclusive: false },
            { min: 70, max: 100, inclusive: false },
            { min: 100, max: 139, inclusive: false },
            { min: 139, max: 180, inclusive: false },
            { min: 180, max: Infinity, inclusive: false },
          ];
          const LABELS = ['1 week', '2 weeks', '3 weeks', '1 month', '1-2 months', '2-3 months', '3-4 months', '4-5 months', '6+ months'];
          const rangeIdx = DELIVERY_RANGES.findIndex(({ min, max, inclusive }) =>
            inclusive ? timeDifferenceInDays >= min && timeDifferenceInDays <= max : timeDifferenceInDays > min && timeDifferenceInDays <= max
          );
          const range = rangeIdx >= 0 ? LABELS[rangeIdx] : null;
          deliveryTime = range ?? (isUnitex ? '10-14 business days' : '1-3 business days');

          ELM_dispatchDate && (ELM_dispatchDate.innerHTML = deliveryTime);
        }

        const [day, month, year] = eta?.split('/') ?? [];
        const parts = day && month && year
          ? new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }).replace(',', '').split(' ')
          : [];
        const outputInfoDate = parts.length >= 3 ? `${parts[1]} ${parts[0]} ${parts[2]}` : '';

        if (timeDifferenceInDays >= 0 && inventoryPolicy === 'continue') {
          ELM_pdpDeliveryInfoValue && (ELM_pdpDeliveryInfoValue.innerHTML = `in ${deliveryTime}`);
          ELM_pdpDeliveryInfo?.classList.remove('hidden');
          ELM_pdpDeliveryInfoSection?.classList.remove('hidden');
          ELM_pdpSoldOutInfo?.classList.add('hidden');
          ELM_dispatchInfo?.classList.remove('hidden');
          ELM_dispatchInfoSoldout?.classList.add('hidden');
          ELM_preOrderText?.classList.add('soldout_available');
          ELM_preOrderText?.classList.remove('hidden');
          $('.input-row .btn-cart').show();
        } else {
          if (isMaProduct) {
            deliveryTime = '1-3 business days';
            ELM_dispatchDate && (ELM_dispatchDate.innerHTML = deliveryTime);
            const infoToday = new Date();
            const infoEndDate = new Date(infoToday);
            infoEndDate.setDate(infoToday.getDate() + 3);
            const day = infoEndDate.getDay();
            if (day === 6) {
              infoEndDate.setDate(infoEndDate.getDate() + 2);
            } else if (day === 0) {
              infoEndDate.setDate(infoEndDate.getDate() + 1);
            }
            const infoMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const infoDeliDates = `between ${infoToday.getDate()} ${infoMonths[infoToday.getMonth()]} and ${infoEndDate.getDate()} ${infoMonths[infoEndDate.getMonth()]}`;
            ELM_pdpDeliveryInfoValue && (ELM_pdpDeliveryInfoValue.innerHTML = infoDeliDates);
            ELM_pdpDeliveryInfo?.classList.remove('hidden');
            ELM_pdpDeliveryInfoSection?.classList.remove('hidden');
            ELM_pdpSoldOutInfo?.classList.add('hidden');
          } else {
            ELM_dispatchDate && (ELM_dispatchDate.innerHTML = deliveryTime);
            ELM_pdpDeliveryInfoSection?.classList.add('hidden');
            ELM_pdpSoldOutInfo?.classList.remove('hidden');
          }

          if ((!eta || !eta.length) && inventoryQuantity <= 0) {
            ELM_preOrderText?.classList.add('soldout_available');
            ELM_preOrderText?.classList.remove('hidden');
            ELM_dispatchInfo?.classList.add('hidden');
            ELM_dispatchInfoSoldout?.classList.remove('hidden');
            ELM_pdpDeliveryInfo?.classList.add('hidden');
            ELM_pdpDeliveryInfoSection?.classList.add('hidden');
            ELM_pdpSoldOutInfo?.classList.remove('hidden');
          }
          if (inventoryQuantity > 0) {
            ELM_preOrderText?.classList.add('hidden');
          }
        }

        if (variant.available == false){
          ELM_pdpDeliveryInfoSection?.classList.add('hidden');
          ELM_pdpSoldOutInfo?.classList.remove('hidden');
          ELM_preOrderText?.classList.remove('hidden');
        }

        if (ELM_inputProperties) {
          if (timeDifferenceInDays < 0 && variant?.inventory_policy === 'continue' && inventoryQuantity <= 0){
            ELM_inputProperties.value = 'late-eta';
          } else if (timeDifferenceInDays >= 0 && variant?.inventory_policy === 'continue') {
            ELM_inputProperties.value = outputInfoDate;
          } else {
            ELM_inputProperties.value = '';
          }
        }
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
